import { formatMagnitude } from '@dawn/core/utils';
import { ACCESS_LEGITIMACY_SCOPE_CONTRACT, type SensitivityOrdinal } from '@dawn/feature-config';
import {
  AccessDiscoveryBody,
  type AccessDiscoveryBodyDefaultData,
  type AccessDiscoveryBodyDrillDownData,
  type IdentityKind,
  type ShowAccessEventsContext,
} from '@dawn/ui';
import { useEffect, useMemo, useState } from 'react';

import type { WidgetConfig } from '@/config/dashboard.types';
import { useDrillToAccessDiscovery } from '@/hooks/use-drill-to-access-discovery';
import {
  idsWithoutEventScope,
  isFoldNodeId,
  nodeAxis,
  resolveScopeFromNode,
  USER_TYPE_BY_KIND,
  type DrillNode,
} from '@/lib/access-legitimacy-scope';

import { WidgetShell } from '../WidgetShell';

function FilesMetricIcon() {
  return (
    <svg
      width="15.32"
      height="18.77"
      viewBox="0 0 15.3184 18.7723"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.81798 0.75H2.47723C2.01914 0.75 1.57981 0.931976 1.2559 1.2559C0.931976 1.57981 0.75 2.01914 0.75 2.47723V16.2951C0.75 16.7532 0.931976 17.1925 1.2559 17.5165C1.57981 17.8404 2.01914 18.0223 2.47723 18.0223H12.8406C13.2987 18.0223 13.7381 17.8404 14.062 17.5165C14.3859 17.1925 14.5679 16.7532 14.5679 16.2951V5.4999L9.81798 0.75Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.38672 0.75V5.9317H14.5684"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NhiMetricIcon() {
  return (
    <svg
      width="22.55"
      height="16.75"
      viewBox="0 0 22.5541 16.7508"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.6728 16.0008H5.87674C4.38621 16.0008 3.17773 14.7453 3.17773 13.1969V7.58906C3.17773 6.0406 4.38621 4.78516 5.87674 4.78516H16.6728C18.1633 4.78516 19.3718 6.0406 19.3718 7.58906V13.1969C19.3718 14.7453 18.1633 16.0008 16.6728 16.0008Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <ellipse cx="8.71169" cy="10.3929" rx="0.944113" ry="0.980806" fill="currentColor" />
      <ellipse cx="13.8406" cy="10.3929" rx="0.944113" ry="0.980806" fill="currentColor" />
      <path
        d="M11.2773 0.75L11.2773 4.78511"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21.8041 10.3929H19.375"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.17911 10.3929H0.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PersonMetricIcon({ className }: { className?: string }) {
  return (
    <svg
      width="19.20"
      height="20.04"
      viewBox="0 0 19.2023 20.0419"
      fill="none"
      aria-hidden="true"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9.60122" cy="5.59536" r="4.84536" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M18.4523 20.0419C18.4523 15.1536 14.4895 11.1908 9.60113 11.1908C4.71279 11.1908 0.75 15.1536 0.75 20.0419"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

interface TaxonomyRef {
  id: string;
  label: string;
}

interface DataContextRow {
  id: string;
  label: string;
  files: number;
  nhi: number;
  hi: number;
  external: number;
  isHighlighted?: boolean;
}

interface DataCategory extends DataContextRow, Omit<DrillNode, 'id' | 'label'> {
  parentId: string;
}

interface SensitivityCount {
  sensitivityId: string;
  label: string;
  count: number;
}

interface RegulationCount {
  regulationId: string;
  label: string;
  count: number;
}

interface FlowConnectionPayload {
  fromId: string;
  toId: string;
  thickness: number;
  fileCount: number;
  distinctHICount: number;
  distinctNHICount: number;
  distinctExternalCount: number;
  sensitivityBreakdown: SensitivityCount[];
  regulationBreakdown: RegulationCount[];
  sensitivityScore: SensitivityOrdinal; // V3
}

interface DrillLevel {
  parentLabel: string;
  parentId: string | null;
  categories: DataCategory[];
  flowConnections: FlowConnectionPayload[];
}

interface AccessDiscoveryResponseData {
  asOf: string;
  totalFiles: number;
  nhiAccess: number;
  hiAccess: number;
  externalAccess: number;
  identityColumn: TaxonomyRef[];
  dataContext: DataContextRow[];
  flowConnections: FlowConnectionPayload[];
  drillDowns: Record<string, DrillLevel>; // V4: required, hard-rejected when missing or malformed
}

/**
 * Returns null when input is not an object, any required default-view field is missing,
 * OR when drillDowns is missing / not an object / contains a malformed entry.
 * Hard-reject ensures server-side regressions fail loudly.
 */
/**
 * Narrowing runs inside render, so every element check has to guard the element
 * itself first: reading a field off a null throws past the widget's own error
 * state instead of rejecting the payload the way the rest of this function does.
 */
function everyFlowValid(value: unknown): boolean {
  if (!Array.isArray(value)) return false;
  return value.every((entry) => {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return false;
    const score = (entry as Record<string, unknown>).sensitivityScore;
    return Number.isFinite(score) && (score as number) >= 1 && (score as number) <= 5;
  });
}

const levelLabel = (dd: Record<string, unknown>): unknown =>
  dd.parentLabel ?? dd.parentDepartmentLabel;
const categoryParent = (c: Record<string, unknown>): unknown => c.parentId ?? c.parentDepartmentId;

/**
 * Collapses both spellings onto the current one, so nothing downstream has to
 * know the rename happened.
 */
function normalizeDrillDowns(drillDowns: Record<string, unknown>): Record<string, DrillLevel> {
  const out: Record<string, DrillLevel> = {};
  for (const [id, entry] of Object.entries(drillDowns)) {
    const dd = entry as Record<string, unknown>;
    out[id] = {
      ...(dd as unknown as DrillLevel),
      parentLabel: levelLabel(dd) as string,
      parentId: (dd.parentId ?? dd.parentDepartmentId ?? null) as string | null,
      categories: (dd.categories as Array<Record<string, unknown>>).map((c) => ({
        ...(c as unknown as DataCategory),
        parentId: categoryParent(c) as string,
      })),
    };
  }
  return out;
}

function narrowAccessDiscoveryData(input: unknown): AccessDiscoveryResponseData | null {
  if (input === null || typeof input !== 'object') return null;
  const candidate = input as Record<string, unknown>;
  if (typeof candidate.asOf !== 'string') return null;
  if (typeof candidate.totalFiles !== 'number') return null;
  if (typeof candidate.nhiAccess !== 'number') return null;
  if (typeof candidate.hiAccess !== 'number') return null;
  if (typeof candidate.externalAccess !== 'number') return null;
  if (!Array.isArray(candidate.identityColumn)) return null;
  if (!Array.isArray(candidate.dataContext)) return null;
  if (!everyFlowValid(candidate.flowConnections)) return null;

  // V4: validate drillDowns
  if (candidate.drillDowns === undefined || candidate.drillDowns === null) return null;
  if (typeof candidate.drillDowns !== 'object' || Array.isArray(candidate.drillDowns)) return null;
  const drillDowns = candidate.drillDowns as Record<string, unknown>;
  for (const entry of Object.values(drillDowns)) {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return null;
    const dd = entry as Record<string, unknown>;
    // Either spelling is accepted while the rename rolls out: an API from before
    // it sends only the old names, and deployments are not simultaneous. A tier
    // with no parent link at all is a root, which is all the old API ever sent.
    if (typeof levelLabel(dd) !== 'string') return null;
    const ddParent = dd.parentId ?? dd.parentDepartmentId ?? null;
    if (ddParent !== null && typeof ddParent !== 'string') return null;
    if (!Array.isArray(dd.categories)) return null;
    // Element shape matters now that a category id is also a key into
    // drillDowns: a category with no id cannot be matched to its tier, and a
    // malformed parentId silently detaches a whole subtree from the trail.
    const categories = dd.categories as Array<unknown>;
    if (
      !categories.every((entry): boolean => {
        // Guard the element itself first: narrowing runs inside render, so
        // reading a field off a null would throw past the widget's own error
        // state instead of rejecting the payload the way every other branch does.
        if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return false;
        const c = entry as Record<string, unknown>;
        const cParent = categoryParent(c);
        return (
          typeof c.id === 'string' &&
          c.id !== '' &&
          typeof cParent === 'string' &&
          cParent !== '' &&
          typeof c.label === 'string' &&
          Number.isFinite(c.files) &&
          Number.isFinite(c.nhi) &&
          Number.isFinite(c.hi) &&
          Number.isFinite(c.external)
        );
      })
    )
      return null;
    if (!everyFlowValid(dd.flowConnections)) return null;
  }

  return {
    ...(candidate as unknown as AccessDiscoveryResponseData),
    drillDowns: normalizeDrillDowns(drillDowns),
  };
}

function mapToDefaultData(data: AccessDiscoveryResponseData): AccessDiscoveryBodyDefaultData {
  return {
    totalFiles: data.totalFiles,
    nhiAccess: data.nhiAccess,
    hiAccess: data.hiAccess,
    externalAccess: data.externalAccess,
    identityColumn: data.identityColumn,
    dataContext: data.dataContext,
    flowConnections: data.flowConnections.map((conn) => ({
      fromId: conn.fromId,
      toId: conn.toId,
      thickness: conn.thickness,
      fileCount: conn.fileCount,
      distinctHICount: conn.distinctHICount,
      distinctNHICount: conn.distinctNHICount,
      distinctExternalCount: conn.distinctExternalCount,
      sensitivityScore: conn.sensitivityScore,
      sensitivityBreakdown: conn.sensitivityBreakdown.map((s) => ({
        id: s.sensitivityId,
        label: s.label,
        count: s.count,
      })),
      regulationBreakdown: conn.regulationBreakdown.map((r) => ({
        id: r.regulationId,
        label: r.label,
        count: r.count,
      })),
    })),
  };
}

function mapToDrillDownData(
  drillDown: DrillLevel,
  trail: readonly { id: string; label: string }[]
): AccessDiscoveryBodyDrillDownData {
  return {
    trail,
    categories: drillDown.categories,
    flowConnections: drillDown.flowConnections.map((conn) => ({
      fromId: conn.fromId,
      toId: conn.toId,
      thickness: conn.thickness,
      fileCount: conn.fileCount,
      distinctHICount: conn.distinctHICount,
      distinctNHICount: conn.distinctNHICount,
      distinctExternalCount: conn.distinctExternalCount,
      sensitivityScore: conn.sensitivityScore,
      sensitivityBreakdown: conn.sensitivityBreakdown.map((s) => ({
        id: s.sensitivityId,
        label: s.label,
        count: s.count,
      })),
      regulationBreakdown: conn.regulationBreakdown.map((r) => ({
        id: r.regulationId,
        label: r.label,
        count: r.count,
      })),
    })),
  };
}

function AccessDiscoveryWidgetBody({
  defaultRaw,
  widgetId,
}: {
  defaultRaw: AccessDiscoveryResponseData;
  widgetId: string;
}) {
  const drillToAccessDiscovery = useDrillToAccessDiscovery(widgetId);
  const [drillPath, setDrillPath] = useState<readonly string[]>([]);

  // A department always drills; below it a node drills only into cluster
  // children, so the drill ends at the leaf cluster.
  const drillableIds = useMemo(() => {
    return new Set(
      Object.entries(defaultRaw.drillDowns)
        .filter(
          ([, tier]) =>
            tier.parentId === null ||
            tier.categories.some(
              (category) => nodeAxis(category) === 'data_cluster' && !isFoldNodeId(category.id)
            )
        )
        .map(([id]) => id)
    );
  }, [defaultRaw]);

  // A filter change republishes drillDowns, and a tier the user is standing in
  // can vanish from it. Keep the longest prefix that still resolves, still drills
  // and still hangs together, so the view falls back to a shallower tier rather than to an
  // empty one.
  const resolvedTrail = useMemo(() => {
    const trail: { id: string; label: string }[] = [];
    for (const [depth, id] of drillPath.entries()) {
      const level = defaultRaw.drillDowns[id];
      if (level === undefined || !drillableIds.has(id)) break;
      if (level.parentId !== (depth === 0 ? null : (drillPath[depth - 1] ?? null))) break;
      trail.push({ id, label: level.parentLabel });
    }
    return trail;
  }, [drillPath, defaultRaw, drillableIds]);

  // Commit the truncation. resolvedTrail only narrows the view; leaving drillPath
  // long means a tier that comes back re-enters itself with no user action.
  useEffect(() => {
    if (resolvedTrail.length === drillPath.length) return;
    setDrillPath(resolvedTrail.map((step) => step.id));
  }, [resolvedTrail, drillPath.length]);

  const drillDownData = useMemo(
    () =>
      resolvedTrail.length === 0
        ? null
        : mapToDrillDownData(
            defaultRaw.drillDowns[resolvedTrail[resolvedTrail.length - 1]!.id]!,
            resolvedTrail
          ),
    [resolvedTrail, defaultRaw]
  );

  const defaultData = useMemo(() => mapToDefaultData(defaultRaw), [defaultRaw]);

  const noEventsIds = useMemo(
    () =>
      idsWithoutEventScope({
        trail: resolvedTrail,
        drillDowns: defaultRaw.drillDowns,
        identityColumn: defaultRaw.identityColumn,
        dataContext: defaultRaw.dataContext,
      }),
    [defaultRaw, resolvedTrail]
  );

  // The widget holds the tree, so it resolves the scope and the grid stays
  // ignorant of the chart's id scheme. Null is a surface no fact column names.
  const scopeOf = (identityId: string | null, dataContextId: string | null) => {
    const scope = resolveScopeFromNode({
      identityId,
      dataContextId,
      trail: resolvedTrail,
      drillDowns: defaultRaw.drillDowns,
      identityColumn: defaultRaw.identityColumn,
      dataContext: defaultRaw.dataContext,
    });
    return scope === null ? null : { ...scope, scopeContract: ACCESS_LEGITIMACY_SCOPE_CONTRACT };
  };
  // Every drill from the chart ends the grid's window at the chart's own cutoff and, as the
  // chart does, counts analysed files only.
  const drill = (link: Parameters<typeof drillToAccessDiscovery>[0]) =>
    drillToAccessDiscovery({ ...link, asOf: defaultRaw.asOf, analysedOnly: true });
  const handleShowAccessEvents = (ctx: ShowAccessEventsContext) => {
    const scope = scopeOf(ctx.identityId, ctx.dataContextId);
    // noEventsIds withholds the action wherever this is null.
    if (scope === null) return;
    drill({ scope });
  };
  // An identity count (TDEV-1116): its data row's scope, or none for the header
  // totals, narrowed to that one user type. A row with no scope never widens to all.
  const handleUserTypeClick = (nodeId: string | null, kind: IdentityKind) => {
    const scope = nodeId === null ? undefined : scopeOf(null, nodeId);
    if (scope === null) return;
    drill({ ...(scope === undefined ? {} : { scope }), userTypes: [USER_TYPE_BY_KIND[kind]] });
  };

  return (
    <AccessDiscoveryBody
      defaultData={defaultData}
      drillDownData={drillDownData}
      drillableIds={drillableIds}
      noEventsIds={noEventsIds}
      totalFilesIcon={<FilesMetricIcon />}
      nhiAccessIcon={<NhiMetricIcon />}
      hiAccessIcon={<PersonMetricIcon />}
      externalAccessIcon={<PersonMetricIcon className="text-error" />}
      onDrillDown={(nodeId) => setDrillPath([...resolvedTrail.map((step) => step.id), nodeId])}
      onExitDrillDown={(toDepth) =>
        setDrillPath(resolvedTrail.slice(0, toDepth).map((step) => step.id))
      }
      onShowAccessEvents={handleShowAccessEvents}
      onUserTypeClick={handleUserTypeClick}
      formatNumber={formatMagnitude}
    />
  );
}

export function AccessDiscoveryWidget({ config }: { config: WidgetConfig }) {
  return (
    <WidgetShell
      config={config}
      renderData={(envelopeData) => {
        const defaultRaw = narrowAccessDiscoveryData(envelopeData);
        if (!defaultRaw) return null;
        return <AccessDiscoveryWidgetBody defaultRaw={defaultRaw} widgetId={config.id} />;
      }}
    />
  );
}
